import { useState } from 'react';
import { ResponsiveTable } from '@/components/Table/ResponsiveTable/ResponsiveTable';
import { VehicleOptionsModal } from '@/reports/sections/VehicleSpecs/VehicleOptionsModal/VehicleOptionsModal';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import { HowItWorksCTA } from '@/reports/shared/ReportSection/components';
import {
  VehicleReportVehicleSpecs,
  AutomobileOption,
  AutomobileOptions,
  AutomobileDesign,
} from '@/reports/types/vehicles';
import {
  Accordion,
  Card,
  Grid,
  PureTextButton,
  Stack,
  Table,
  Text,
} from '@/theme/index';
import { capitalizeString } from '@/utils/capitalizeString';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';

interface VehicleSpecs {
  vehicleSpecs: VehicleReportVehicleSpecs;
  vehicleDesign: AutomobileDesign | undefined;
}

function HowItWorksCTAContent() {
  return (
    <Text
      variant="body2"
      fontSize="0.9rem"
      color={(theme) => theme.palette.grey[800]}
    >
      The availability of specific vehicle specifications may vary based on
      factors such as manufacturer transparency, new model releases, and
      individual customizations. Not all vehicles have readily available data,
      as specs are sourced from various channels.
    </Text>
  );
}

export function VehicleSpecs({ vehicleSpecs, vehicleDesign }: VehicleSpecs) {
  const [vehicleOptionsModalOpen, setVehicleOptionsModalOpen] = useState(false);
  const [currentVehicleOption, setCurrentOption] = useState<
    AutomobileOption | undefined
  >();

  const generalData = [
    ['Vehicle type', vehicleSpecs.general?.type],
    ['Made in', vehicleSpecs.general.origin],
  ];

  const priceData = [
    [
      'MSRP',
      vehicleSpecs.price.msrp
        ? commaFormatCurrency(vehicleSpecs.price.msrp)
        : '',
    ],
    [
      'Invoice price',
      vehicleSpecs.price.invoicePrice
        ? commaFormatCurrency(vehicleSpecs.price.invoicePrice)
        : '',
    ],
    [
      'Delivery charges',
      vehicleSpecs.price.deliveryCharges
        ? commaFormatCurrency(vehicleSpecs.price.deliveryCharges)
        : '',
    ],
  ];

  const fuelData = [
    ['Fuel type', vehicleSpecs.fuel.type],
    ['Fuel capacity', vehicleSpecs.fuel.capacity],
    ['Highway mileage', vehicleSpecs.fuel.highwayMileage],
    ['City mileage', vehicleSpecs.fuel.cityMileage],
  ];

  const detailsData = [
    ['Category', vehicleSpecs.details.category],
    ['Size', vehicleSpecs.details.size],
    ['Style', vehicleSpecs.details.style],
    ['Engine', vehicleSpecs.details.engine],
    [
      'Engine size',
      vehicleSpecs.details.engineSize
        ? vehicleSpecs.details.engineSize.toString()
        : '',
    ],
    ['Trim', vehicleSpecs.details.trim],
    ['Steering type', vehicleSpecs.details.steeringType],
    ['Anti-lock brake system', vehicleSpecs.details.antiBrakeSystem],
    ['Overall Length', vehicleSpecs.details.length],
    ['Overall Height', vehicleSpecs.details.height],
    ['Overall Width', vehicleSpecs.details.width],
    [
      'Doors',
      vehicleSpecs.details.doors ? vehicleSpecs.details.doors.toString() : '',
    ],
    [
      'Standard seating',
      vehicleSpecs.details.standardSeating
        ? vehicleSpecs.details.standardSeating.toString()
        : '',
    ],
    ['Transmission type', vehicleSpecs.details.transmissionType],
    ['Driven wheels', vehicleSpecs.details.drivenWheels],
  ];

  const calculateTotalCostOfOptions = (options: AutomobileOptions) => {
    return options.reduce((accumulator, option) => {
      if (option?.equipment) {
        const equipment = findFirstEquipmentWithNumeralValue(option);

        if (equipment?.value) {
          accumulator += parseInt(equipment.value);
        }
      }

      return accumulator;
    }, 0);
  };

  const reserializeOptionsData = (options: AutomobileOptions) => {
    return options.map((option) => {
      const type =
        option.type === 'O' ? 'Option' : option.type === 'P' ? 'Package' : '';
      const cost = getOptionCost(option);
      return [
        <PureTextButton
          variant="text"
          sx={{ fontWeight: option.installed ? 'bold' : 'normal', p: 0 }}
          onClick={() => openVehicleOptionsModal(option)}
          color="success"
        >
          {capitalizeString(option.name)}
        </PureTextButton>,
        type,
        cost ? commaFormatCurrency(cost) : '$-',
      ];
    });
  };

  const totalCostOfInstalledOptions = calculateTotalCostOfOptions(
    vehicleSpecs.installedOptions
  );

  function getOptionCost(option: AutomobileOption) {
    if (!option?.equipment) {
      return 0;
    }

    const equipment = findFirstEquipmentWithNumeralValue(option);

    return equipment?.value ? parseInt(equipment.value) : 0;
  }

  function findFirstEquipmentWithNumeralValue(option: AutomobileOption) {
    return option.equipment.find((element) => {
      return (
        element?.attribute === 'Amount USD' &&
        element?.group === 'Price & Cost' &&
        Boolean(element?.value)
      );
    });
  }

  function openVehicleOptionsModal(option: AutomobileOption) {
    setCurrentOption(option);
    setVehicleOptionsModalOpen(true);
  }

  function closeVehicleOptionsModal() {
    setCurrentOption(undefined);
    setVehicleOptionsModalOpen(false);
  }

  const reserializedInstalledOptionsData = reserializeOptionsData(
    vehicleSpecs.installedOptions
  );
  const reserializedAvailableOptionsData = reserializeOptionsData(
    vehicleSpecs.availableOptions
  );
  const displayInstalledAndAvailableSubsection =
    Boolean(reserializedInstalledOptionsData.length) ||
    Boolean(reserializedAvailableOptionsData.length);

  const equipmentSortedIntoGroups: {
    [key: string]: (string | JSX.Element)[][];
  } = {};

  vehicleSpecs.equipments?.forEach((equipment) => {
    if (equipment.group && equipment.name) {
      const tableRow = [
        equipment.name,
        equipment.attribute || '-',
        equipment.location || '-',
        equipment.value || '-',
      ].map((cell) => {
        return (
          <Text variant="body2" sx={{ p: 1 }}>
            {cell}
          </Text>
        );
      });

      equipmentSortedIntoGroups[equipment.group]?.length
        ? equipmentSortedIntoGroups[equipment.group].push(tableRow)
        : (equipmentSortedIntoGroups[equipment.group] = [tableRow]);
    }
  });
  const equipmentSortedKeys = Object.keys(equipmentSortedIntoGroups);

  const hasGeneralData =
    generalData.filter((row) => Boolean(row[1])).length > 0;
  const hasPriceData = priceData.filter((row) => Boolean(row[1])).length > 0;
  const hasFuelData = fuelData.filter((row) => Boolean(row[1])).length > 0;
  const hasDetailsData =
    detailsData.filter((row) => Boolean(row[1])).length > 0;
  const hasSpecs =
    hasGeneralData || hasPriceData || hasFuelData || hasDetailsData;
  const hasContent =
    hasSpecs ||
    displayInstalledAndAvailableSubsection ||
    Boolean(equipmentSortedKeys.length);

  const currentColor = vehicleDesign?.current_color;
  const currentColorData = [];
  if (currentColor) {
    const genericColor = currentColor.generic_name
      ? `(${currentColor.generic_name})`
      : '';
    currentColorData.push([
      'Installed color',
      currentColor.name ? `${currentColor.name} ${genericColor}` : '',
    ]);
    currentColorData.push(['Paint code', currentColor.code || '']);
  }

  const colors = vehicleDesign?.colors?.map((color) => {
    const genericColor = color.generic_name ? `(${color.generic_name})` : '';
    return `${color.name} ${genericColor}`;
  });

  return (
    <ReportSection
      title="Vehicle Specifications"
      showTopBar
      hasContent={hasContent}
      sectionId="vehicle-specs"
      data-testid="vehicle-specs-report-section"
      howItWorks={<HowItWorksCTA content={HowItWorksCTAContent()} />}
    >
      {hasSpecs && (
        <Card>
          <Text variant="h3" data-testid="vehicle-specs-general-title">
            {vehicleSpecs.general.ymm}
          </Text>
          <Table
            rows={generalData}
            sectionId="vehicle-specs-card-general"
            className="flipped"
          />
          <Text variant="h4" mt={2}>
            Price
          </Text>
          <Table
            rows={priceData}
            sectionId="vehicle-specs-card-price"
            className="flipped striped"
          />
          <Text variant="h4" mt={2}>
            Fuel
          </Text>
          <Table
            rows={fuelData}
            sectionId="vehicle-specs-card-fuel"
            className="flipped striped"
          />
          <Text variant="h4" mt={2}>
            Vehicle details
          </Text>
          <Table
            rows={detailsData}
            sectionId="vehicle-specs-card-details"
            className="flipped striped"
          />
        </Card>
      )}
      {displayInstalledAndAvailableSubsection && (
        <Card>
          {Boolean(reserializedInstalledOptionsData.length) && (
            <>
              <Text variant="h3">Installed options and packages</Text>
              <Text mb={2}>
                Total cost of installed options and packages:{' '}
                <Text fontWeight="bold" component="span">
                  {commaFormatCurrency(totalCostOfInstalledOptions)}
                </Text>
              </Text>
              <ResponsiveTable
                tableData={reserializedInstalledOptionsData}
                headers={['Item', 'Type', 'Cost']}
                sectionId="vehicle-specs-data"
              />
            </>
          )}
          {Boolean(reserializedAvailableOptionsData.length) && (
            <>
              <Text variant="h3" mt={4}>
                Available options
              </Text>
              <Text mb={2}>
                Available options is a list of all possible options that were
                not installed on this vehicle
              </Text>
              <Grid container>
                {reserializedAvailableOptionsData.map((option, index) => {
                  return (
                    <Grid
                      item
                      lg={4}
                      md={4}
                      sm={4}
                      xs={12}
                      key={index}
                      padding="10px 10px 10px 0"
                    >
                      {option[0]}
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </Card>
      )}
      {Boolean(equipmentSortedKeys.length) && (
        <Card>
          <Text variant="h3">Equipment specifications</Text>
          {(currentColor || colors) && (
            <Accordion display="Color" key="color">
              <Table
                rows={currentColorData}
                sectionId="vehicle-specs-card-color"
                sx={{
                  'td:last-of-type': { fontWeight: 'bold' },
                  'td:first-of-type': { width: '200px' },
                  td: { fontSize: '1.2rem' },
                }}
              />
              {colors && (
                <Stack spacing={1} mt={2}>
                  <Text variant="h5" mb={0}>
                    List of available colors
                  </Text>
                  <Text variant="body2" fontSize="1rem">
                    Available colors are all possible colors that were not
                    installed on this vehicle
                  </Text>
                  <Grid container>
                    {colors.map((color, index) => (
                      <Grid
                        item
                        lg={4}
                        md={4}
                        sm={4}
                        xs={12}
                        key={index}
                        paddingY={2}
                        fontSize="0.9rem"
                      >
                        {color}
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              )}
            </Accordion>
          )}
          {equipmentSortedKeys.map((key, index) => {
            return (
              <Accordion display={key} key={index}>
                <ResponsiveTable
                  headers={['Name', 'Attribute', 'Location', 'Value']}
                  tableData={equipmentSortedIntoGroups[key]}
                  sectionId="equipment"
                />
              </Accordion>
            );
          })}
        </Card>
      )}
      {currentVehicleOption && (
        <VehicleOptionsModal
          open={vehicleOptionsModalOpen}
          currentOption={currentVehicleOption}
          handleClose={closeVehicleOptionsModal}
        />
      )}
    </ReportSection>
  );
}
